"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  BookOpen,
  Sparkles,
  ArrowRight,
  Mic,
  Crown,
  Globe2,
  Server,
  Database,
  Layers,
  Network,
  Building2,
  Users,
  Shield,
  Compass,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { TreeBlock } from "@/lib/blocks/tree-block";
import { FeatureGridBlock } from "@/lib/blocks/feature-grid-block";
import { AccordionBlock } from "@/lib/blocks/accordion-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

export default function EGovernancePage() {
  return (
    <>
      <PageHero
        icon={BookOpen}
        overline="Glossary · eGovernance"
        overlineClassName="text-lime"
        title={
          <>
            The Digital Operating System for <span className="text-gradient-premium">Modern Government</span>
          </>
        }
        subtitle="The use of digital technology, data intelligence, and automated systems to deliver government services, implement policy, engage citizens, and manage public institutions at national scale. Not digitizing existing bureaucracy. Fundamentally reimagining how government operates."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Glossary", href: "/glossary/" }, { label: "eGovernance" }]}
        badge="Definition · 18 countries · 900M+ citizens"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.lime.pill} mb-5`}>
            <Sparkles className="size-3.5 text-lime" />
            <span className="text-overline text-lime">Definition</span>
          </div>
          <h2 className="text-display-md mb-5">What eGovernance is.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            <span className="font-semibold text-foreground">eGovernance</span> (electronic governance) is the use of digital technology, data intelligence, and automated systems to deliver government services, implement policy, engage citizens, and manage public institutions at national scale. It replaces paper-based bureaucratic processes with integrated digital infrastructure — covering policy formation, legislative intelligence, administrative operations, service delivery, fraud detection, and citizen feedback within a unified platform architecture.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            The term encompasses the complete lifecycle of digital government: from AI-powered policy modeling and legislative tracking, through single-window citizen service delivery, to real-time scheme implementation monitoring and participatory feedback systems.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            At its most comprehensive, eGovernance operates across six technology layers: Abstraction, Service, Orchestration, Application, Presentation, and Integration — ensuring that every government function, from treasury management to welfare disbursement to healthcare delivery, runs on a unified digital backbone.
          </p>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Layers className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Six-Layer Stack</span>
          </div>
          <h2 className="text-display-md mb-4">A complete eGovernance platform.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            A complete eGovernance platform operates across six technology layers, each performing essential functions that ensure the platform functions as a unified system.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { num: "Layer 1", title: "Abstraction", body: "Schema abstraction provides unified data models across all government departments. Protocol translation normalizes SOAP, REST, GraphQL, and event-streaming protocols. Sovereignty enforcement points verify data residency requirements.", accent: "red", icon: Database },
            { num: "Layer 2", title: "Service", body: "Federated identity management supporting SAML 2.0, OAuth 2.0, OpenID Connect, and government-specific PKI. Multi-channel notification delivery across email, SMS, push, IVR, WhatsApp.", accent: "purple", icon: Server },
            { num: "Layer 3", title: "Orchestration", body: "Cross-domain workflow templates for common cross-government scenarios. Resource pooling management for shared infrastructure. Dependency graph engine for automated detection of implicit dependencies.", accent: "cyan", icon: Network },
            { num: "Layer 4", title: "Application", body: "Policy development suite, administration management suite, service delivery suite, and engagement suite — each containing domain-specific modules configured for government operations.", accent: "emerald", icon: Building2 },
            { num: "Layer 5", title: "Presentation", body: "Responsive web with WCAG 2.1 AA accessibility. Native mobile applications with offline capability. Government kiosk interface for rural deployment. IVR voice interface supporting multiple languages.", accent: "amber-gold", icon: Globe2 },
            { num: "Layer 6", title: "Integration", body: "Enterprise Service Bus patterns, message queue bridges, batch file interfaces for legacy systems, database connectors, and API gateways — ensuring no existing government system is orphaned.", accent: "denim", icon: Shield },
          ].map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`group glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shadow-lg`}>
                    <p.icon className="size-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className={`text-overline ${a.text}`}>{p.num}</span>
                    <h3 className={`text-heading-md mb-2 ${a.text}`}>{p.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{p.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Globe2 className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">eGovernance vs. Digital Transformation</span>
          </div>
          <h2 className="text-display-md mb-4">What makes eGovernance distinct.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The distinction matters. Private-sector digital transformation optimizes for profit and efficiency. eGovernance must simultaneously optimize for service delivery, citizen satisfaction, fraud prevention, and democratic accountability.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className={`glass-card rounded-2xl p-6 bg-gradient-to-br ${ACCENT["amber-gold"].bg} to-transparent border ${ACCENT["amber-gold"].border}`}>
            <h3 className={`text-heading-md mb-2 text-amber-gold`}>Private-Sector Digital Transformation</h3>
            <p className="text-body-sm text-muted-foreground leading-relaxed">Optimizes for profit and efficiency. Operates on licensed infrastructure controlled by technology vendors. Adoption of digital technology to improve operations across any organization.</p>
          </div>
          <div className={`glass-card rounded-2xl p-6 bg-gradient-to-br ${ACCENT.lime.bg} to-transparent border ${ACCENT.lime.border}`}>
            <h3 className={`text-heading-md mb-2 text-lime`}>eGovernance</h3>
            <p className="text-body-sm text-muted-foreground leading-relaxed">Simultaneously optimizes for service delivery, citizen satisfaction, fraud prevention, policy implementation, data sovereignty, and democratic accountability. Must run on government-owned infrastructure with citizen data that never transits foreign jurisdictions.</p>
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Network className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Related Terms</span>
          </div>
          <h2 className="text-display-md mb-4">The conceptual map.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { term: "Digital Conglomerate", href: "/glossary/digital-conglomerate/", desc: "The organizational form that operates eGovernance platforms.", accent: "purple" },
            { term: "Strategic Sovereignty", href: "/glossary/strategic-sovereignty/", desc: "The integrated discipline eGovernance supports.", accent: "red" },
            { term: "Penta P's Framework", href: "/glossary/penta-ps-framework/", desc: "The five-domain model that includes the Policy pillar.", accent: "cyan" },
            { term: "Sovereign Operations Architecture", href: "/glossary/sovereign-operations-architecture/", desc: "The methodology that operationalizes eGovernance.", accent: "emerald" },
            { term: "Operational Excellence", href: "/glossary/operational-excellence/", desc: "The engineered system eGovernance requires.", accent: "amber-gold" },
            { term: "Crisis Transformation", href: "/glossary/crisis-transformation/", desc: "The perception-pillar discipline for emergencies.", accent: "coral" },
          ].map((t, i) => {
            const a = ACCENT[t.accent];
            return (
              <motion.div
                key={t.href}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
              >
                <Link href={t.href} className="block h-full group">
                  <div className={`relative glass-card rounded-2xl p-5 h-full bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}>
                    <h3 className={`text-heading-sm mb-2 ${a.text}`}>{t.term}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed mb-3">{t.desc}</p>
                    <span className={`inline-flex items-center gap-1.5 text-caption font-semibold ${a.text} group-hover:gap-2.5 transition-all`}>
                      Read <ArrowRight className="size-3" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Network className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">E-Governance Tree</span>
          </div>
          <h2 className="text-display-md mb-4">The eGovernance functional tree.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            eGovernance is organized into five functional roots — Public Administration, Law and Justice, Health and Social Services, Education and Skills, and Infrastructure. Each root branches into specific government function categories and operational modules.
          </p>
        </div>
        <TreeBlock
          entries={[
            { id: "eg1", label: "Public Administration & Finance", body: "Treasury management, budget allocation, public procurement, audit, and administrative services. The foundation of government operations.", accent: "lime", children: [
              { id: "eg1a", label: "Treasury & Budget", body: "Real-time budget tracking, expenditure analysis, and fiscal discipline mechanisms. Supports sovereign-grade financial governance at national scale." },
              { id: "eg1b", label: "Public Procurement", body: "Transparent procurement processes, vendor management, and contract lifecycle. Eliminates corruption vectors through systematic visibility." },
            ]},
            { id: "eg2", label: "Law & Justice", body: "Court management, case tracking, legal aid, prison administration, and judicial intelligence. The operational backbone of rule of law.", accent: "emerald", children: [
              { id: "eg2a", label: "Court Management", body: "Digital case filing, scheduling, evidence management, and judicial analytics. Reduces case backlog and improves judicial efficiency." },
              { id: "eg2b", label: "Legal Aid & Access", body: "Citizen-facing legal services, eligibility determination, and legal representation coordination. Expands access to justice at scale." },
            ]},
            { id: "eg3", label: "Health & Social Services", body: "Hospital management, welfare distribution, social security, and citizen health records. The most direct citizen-facing government functions.", accent: "cyan", children: [
              { id: "eg3a", label: "Hospital Management", body: "Patient records, appointment scheduling, supply chain, and clinical decision support. Improves outcomes while reducing costs." },
              { id: "eg3b", label: "Welfare Distribution", body: "Eligibility determination, direct benefit transfer, fraud detection, and impact measurement. The most visible citizen-facing function." },
            ]},
            { id: "eg4", label: "Education & Skills", body: "School management, examination systems, skill development, and educational analytics. Building the human capital foundation.", accent: "purple", children: [
              { id: "eg4a", label: "School Management", body: "Student information systems, attendance tracking, performance analytics, and resource allocation. Improves educational outcomes at scale." },
              { id: "eg4b", label: "Skill Development", body: "Vocational training, certification, employment matching, and lifelong learning platforms. Aligns human capital with economic needs." },
            ]},
            { id: "eg5", label: "Infrastructure & Civic Services", body: "Urban planning, transportation, utilities, electoral systems, and civic engagement. The operational infrastructure of modern government.", accent: "amber-gold", children: [
              { id: "eg5a", label: "Urban & Infrastructure", body: "Smart city platforms, utility management, transportation systems, and urban planning tools. Supports sustainable urban development." },
              { id: "eg5b", label: "Electoral & Civic", body: "Voter registration, election management, civic feedback, and participatory governance. The democratic accountability layer of eGovernance." },
            ]},
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Compass className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Usage Examples</span>
          </div>
          <h2 className="text-display-md mb-4">eGovernance in practice.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Four examples of eGovernance deployment across different national contexts. Each example shows the digital governance platform operating at scale.
          </p>
        </div>
        <FeatureGridBlock
          features={[
            { title: "National Welfare Distribution", body: "A national welfare program serving 120 million citizens. The digital governance platform handles eligibility determination, direct benefit transfer, fraud detection, and impact measurement. The deployment replaced legacy paper-based systems with a unified digital backbone.", accent: "lime" },
            { title: "Citizen Service Delivery", body: "A multi-channel citizen service portal serving millions of users. Web, mobile, IVR, and WhatsApp channels provide unified access to government services. 24/7 availability, WCAG 2.1 AA accessibility, multi-language support.", accent: "emerald" },
            { title: "Healthcare Network Integration", body: "A national healthcare network integrating hospitals, clinics, and pharmacies. Patient records, appointment scheduling, supply chain management, and clinical decision support. Improved outcomes and reduced costs across the system.", accent: "cyan" },
            { title: "Smart City Deployment", body: "A metropolitan smart city platform integrating transportation, utilities, public safety, and citizen services. IoT sensors, predictive analytics, and unified command dashboard. The urban operating system for modern cities.", accent: "purple" },
          ]}
          columns={2}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <BookOpen className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Common Misconceptions</span>
          </div>
          <h2 className="text-display-md mb-4">What eGovernance is not.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Six persistent misconceptions that conflate eGovernance with digitization, automation, or technology adoption. The distinctions are operational, not rhetorical.
          </p>
        </div>
        <AccordionBlock
          items={[
            { q: "Is eGovernance the same as digitization?", a: "No. Digitization converts paper processes to digital. eGovernance reimagines how government operates. The difference: digitization optimizes existing bureaucratic processes. eGovernance replaces the bureaucracy with a citizen-centric operating model." },
            { q: "Is eGovernance the same as eGovernment?", a: "No. eGovernment often refers to digital service delivery — making existing government services available online. eGovernance is broader: it encompasses service delivery, policy formation, institutional transformation, and citizen engagement as an integrated system." },
            { q: "Can eGovernance be achieved through technology alone?", a: "No. Technology is necessary but insufficient. Institutional change, process redesign, citizen trust, and political will are equally critical. Technology without institutional alignment produces expensive systems that no one uses." },
            { q: "Is eGovernance the same as digital transformation?", a: "No. Digital transformation in the private sector optimizes for profit and efficiency. eGovernance must simultaneously optimize for service delivery, citizen satisfaction, fraud prevention, policy implementation, data sovereignty, and democratic accountability." },
            { q: "Does eGovernance require citizen data to be stored in foreign jurisdictions?", a: "No. eGovernance requires sovereign data infrastructure. Citizen data must remain within national borders under government control. Sovereign data residency is a foundational requirement, not a feature." },
            { q: "Is eGovernance a one-time project?", a: "No. eGovernance is a continuous transformation. The platform evolves with citizen needs, technology advances, and institutional capacity. The compounding effect produces increasingly sophisticated governance over time." },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-lime/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-emerald/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Globe2 className="size-10 text-lime mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Engage eGovernance.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              eGovernance is operationalized through advisory engagements, deployment projects, and policy transformation programs. Sovereignty is not a feature — it is a foundational requirement.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/glossary/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <BookOpen className="size-4" />
                Glossary Hub
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Glossary Hub", description: "Ten terms defining sovereign-scale operations.", href: "/glossary/", icon: BookOpen, accent: "cyan" },
          { title: "About Dr. Jyoti Kush", description: "The full story of the operational architect.", href: "/about/", icon: Crown, accent: "red" },
          { title: "FAQ: Platforms", description: "The eGovernance platforms and integration.", href: "/faq/platforms/", icon: Server, accent: "amber-gold" },
          { title: "Strategic Sovereignty", description: "The integrated discipline eGovernance supports.", href: "/glossary/strategic-sovereignty/", icon: Crown, accent: "red" },
        ]}
      />
    </>
  );
}
